import { metrics, registry } from '@geo-admin/shared/contract/api/index.ts';
import { EngineTabs, engineSearch, useEngine } from '../components/EngineTabs.tsx';
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { PageTitle } from '../components/PageTitle.tsx';
import { SampleTable } from '../components/SampleTable.tsx';
import { Button } from '../components/ui/button.tsx';
import { Card } from '../components/ui/card.tsx';
import { ErrorBox, Loading } from '../components/ui/states.tsx';
import { useApi } from '../lib/api.ts';
import { PATHS } from '../lib/paths.ts';

/** 쪽 번호는 주소(search)에 둔다 — 샘플 상세에서 뒤로 오면 보던 쪽이 그대로다 */
export function validateQuerySamplesSearch(search: Record<string, unknown>): { page?: number; engine?: string } {
  const page = Number(search.page);
  return {
    ...(Number.isInteger(page) && page > 1 ? { page } : {}),
    // 엔진 탭(EngineTabs) — 이 검사가 모르는 값은 떨어지므로 함께 통과시킨다
    ...(search.engine === 'gemini' ? { engine: 'gemini' } : {}),
  };
}

/** 질문 한 건의 전체 샘플 — 질문 상세 「최근 샘플」의 「더보기」. 운영자 영역 전용 */
export function QuerySamplesPage() {
  const { id } = useParams({ strict: false }) as { id: string };
  const { page = 1 } = useSearch({ strict: false }) as { page?: number };
  const navigate = useNavigate();
  const engine = useEngine();
  const q = useApi(registry.getQuery, { params: { id } });
  const s = useApi(metrics.querySamples, { params: { id }, query: { page, engine } });

  if (q.isLoading || s.isLoading) return <Loading />;
  if (q.error || s.error) return <ErrorBox error={q.error ?? s.error} />;
  const query = q.data!.query;
  const { total, pageSize, samples } = s.data!;
  const pages = Math.max(1, Math.ceil(total / pageSize));
  const go = (p: number) =>
    navigate({
      to: PATHS.admin.querySamples,
      params: { id },
      search: { ...engineSearch(engine), ...(p > 1 ? { page: p } : {}) },
    });

  return (
    <>
      <PageTitle
        title={query.query}
        description={`${query.brandName} · 전체 샘플 ${total.toLocaleString()}건 · 최신순`}
        actions={
          <span className="flex items-center gap-3">
            <EngineTabs />
            <Link
              to={PATHS.admin.query}
              params={{ id }}
              search={engineSearch(engine)}
              className="text-sm text-slate-500 hover:underline"
            >
              ← 질문 상세
            </Link>
          </span>
        }
      />
      <Card>
        <SampleTable samples={samples} />
      </Card>
      {total > pageSize ? (
        <div className="flex items-center justify-center gap-3 text-sm">
          <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => go(page - 1)}>
            이전
          </Button>
          <span className="text-slate-500">
            {page} / {pages} 쪽
          </span>
          <Button variant="outline" size="sm" disabled={page >= pages} onClick={() => go(page + 1)}>
            다음
          </Button>
        </div>
      ) : null}
    </>
  );
}
